import Image from 'next/image';
import BlogPost from 'components/blog/blog-post';
import Container from 'components/container';
import Link from 'next/link';
import { LIBUM_URL, SALES_MAILTO } from 'shared/constants/globals';
import { BLOG_ITEMS } from 'shared/data/blogs';

const BLOG = BLOG_ITEMS.find((b) => b.id === 'symitar-core-speaks-ai')!;

export default function SymitarCoreSpeaksAi() {
  const title = `${BLOG.title} | Libum`;
  const description = BLOG.description;
  const keywords = BLOG.keywords.join(',');
  const url = `${LIBUM_URL}/blog/${BLOG.id}`;

  return (
    <Container
      title={title}
      description={description}
      keywords={keywords}
      url={url}
      socialCard="blog"
      breadcrumbs={[
        { name: 'Blog', url: `${LIBUM_URL}/blog` },
        { name: BLOG.title, url },
      ]}
      blog={BLOG}
    >
      <div style={{ width: '100%' }}>
        <BlogPost blog={BLOG}>
          <p>
            Your Symitar core has always spoken SOAP. A few years ago, with{' '}
            <Link href="/nexum" target="_blank">
              NexumAPI
            </Link>
            , we taught it JSON. Today, it speaks plain English{' '}
            <Image src="/icons/twitter/sparkles.png" alt="Sparkles" className="icon" width={18} height={18} />
          </p>
          <p>
            We&apos;re introducing <b>NexumAPI MCP</b> — a{' '}
            <Link href="https://modelcontextprotocol.io" target="_blank" rel="noopener noreferrer">
              Model Context Protocol
            </Link>{' '}
            server that lets the AI tools your team <b>already uses</b> talk directly to SymXchange. Ask a question in
            natural language, and your assistant pulls the answer <b>straight from the core</b> — no SOAP, no scripts, no
            spreadsheets in between.
          </p>
          <div className="divider" />
          <h2 className="anchor" id="what-is-nexum-mcp">
            <Link href="#what-is-nexum-mcp">What is NexumAPI MCP?</Link>
          </h2>
          <p>
            MCP is the open standard that lets AI assistants securely connect to real systems and act on your behalf.
            NexumAPI MCP puts SymXchange behind that standard, so <b>Claude</b>, <b>ChatGPT</b>, <b>Cursor</b>,{' '}
            <b>Codex</b>{' '}— or any MCP-compatible client — can read accounts, project loans, and run the operations
            you&apos;d normally script by hand, just by asking.
          </p>
          <p>
            Because it&apos;s built on the same engine as NexumAPI, it covers{' '}
            <b>every SymXchange service, every operation, on any supported version</b> — and it stays in lockstep as your
            core evolves.
          </p>
          <h3 className="anchor" id="what-you-can-do">
            <Link href="#what-you-can-do">What you can do with it</Link>
          </h3>
          <p>
            The old way meant knowing the exact SymXchange service, the operation name, and the precise shape of every
            request — then stitching the responses together yourself. With NexumAPI MCP, you skip all of that. Your
            assistant figures out which operations to call, chains them together when one answer feeds the next, and
            reads the results back in plain language — point it at the core and have a conversation:
          </p>
          <blockquote>&quot;What are the last few transactions for Account # 13824?&quot;</blockquote>
          <figure>
            <video className="blog-video" autoPlay muted loop playsInline preload="metadata">
              <source src="/videos/naas-mcp.webm" type="video/webm" />
              <source src="/videos/naas-mcp.mp4" type="video/mp4" />
            </video>
          </figure>
          <p>
            Behind the scenes, your assistant finds the account, pulls its recent transactions, and hands you a clean
            summary — no SOAP, no scripts. A few more things your team can ask for out of the box:
          </p>
          <ul className="prompts">
            <li>
              <Image src="/icons/twitter/magnifying.png" alt="Magnifying glass" className="icon" width={18} height={18} />{' '}
              &quot;Find the account tied to this card number.&quot;
            </li>
            <li>
              <Image src="/icons/twitter/money.png" alt="Money" className="icon" width={18} height={18} />{' '}
              &quot;Project the monthly payment on a $25,000 auto loan at 6.9% over 60 months.&quot;
            </li>
            <li>
              <Image src="/icons/twitter/memo.png" alt="Memo" className="icon" width={18} height={18} />{' '}
              &quot;Verify check #1234 on this account.&quot;
            </li>
            <li>
              <Image src="/icons/twitter/fire.png" alt="Fire" className="icon" width={18} height={18} />{' '}
              &quot;Run our month-end PowerOn and summarize the output.&quot;
            </li>
            <li>
              <Image
                src="/icons/twitter/mechanical-arm.png"
                alt="Mechanical arm"
                className="icon"
                width={18}
                height={18}
              />{' '}
              &quot;List the batch jobs and their current status.&quot;
            </li>
          </ul>
          <div className="divider" />
          <h2 className="anchor" id="safe-by-design">
            <Link href="#safe-by-design">Safe by design</Link>
          </h2>
          <p>
            This is a banking core, so <b>safe isn&apos;t a feature — it&apos;s the whole point</b>. NexumAPI MCP is{' '}
            <b style={{ color: '#00D68F' }}>read-only out of the box</b>, and every layer around it is locked down{' '}
            <Image src="/icons/twitter/shield.png" alt="Shield" className="icon" width={18} height={18} />
          </p>
          <ul className="safe-layers">
            <li>
              <b>Writes are off by default.</b>
              <span>
                Reads ship first. Create, update, delete, and financial operations only appear if your credit union
                explicitly turns them on.
              </span>
            </li>
            <li>
              <b>Policy controls narrow the surface.</b>
              <span>
                Admins can allow only the MCP tools, SymXchange services, operations, credential types, and user
                identities your team has approved.
              </span>
            </li>
            <li>
              <b>Two separate keys.</b>
              <span>
                A bearer token authorizes the endpoint; your SymXchange credentials are separate and passed by header —
                never typed into a prompt or stored in a chat.
              </span>
            </li>
            <li>
              <b>Confirmation on sensitive actions.</b>
              <span>Anything that moves money or changes a record requires an explicit confirm step before it runs.</span>
            </li>
            <li>
              <b>No raw payloads, ever.</b>
              <span>
                The AI never hand-writes NaaS JSON. Every call is schema-validated against your core&apos;s actual
                definitions before it leaves.
              </span>
            </li>
            <li>
              <b>Every call is logged.</b>
              <span>
                The tool, the member context, and the user behind the request are recorded for a complete audit trail.
              </span>
            </li>
          </ul>
          <div className="divider" />
          <h2 className="anchor" id="who-its-for">
            <Link href="#who-its-for">Who it&apos;s for</Link>
          </h2>
          <p>
            NexumAPI MCP is built for the people who run the core — <b>operations</b>, <b>lending</b>, and{' '}
            <b>member services</b> using AI assistants to move faster — and for <b>developers</b>{' '}building agents and
            internal tools on top of Symitar. It&apos;s not member-facing; it&apos;s a power tool for your team{' '}
            <Image src="/icons/twitter/rocket.png" alt="Rocket" className="icon" width={18} height={18} />
          </p>
          <div className="divider" />
          <h2 className="anchor" id="get-started">
            <Link href="#get-started">Get started</Link>
          </h2>
          <p>
            NexumAPI MCP is <b style={{ color: '#00D68F' }}>available now</b> as an opt-in capability for NexumAPI
            customers, enabled per credit union. Want to give your team a natural-language line to the core? Take a look
            at{' '}
            <Link href="/nexum" target="_blank">
              NexumAPI
            </Link>{' '}
            or <a href={SALES_MAILTO}>reach out to our team</a>{' '}and we&apos;ll get you connected.
          </p>
        </BlogPost>
      </div>
    </Container>
  );
}
